import { useMemo, useState } from 'react';
import type { V2Area } from '../../domain/inspection';
import { linkedTemplateIdsOf, photoGuideStatus, type ShotStatus } from '../../domain/photoGuide';
import { useT } from '../../i18n/i18n';
import { captureFromCameraAction, importFromLibraryAction, type EvidenceTarget } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { inspectionStore, setShotSkippedAction } from '../../state/inspectionStore';
import { Icon, InlineMessage, SheetShell } from '../../ui/components';
import { IconButton } from '../../ui/components';

// 拍照清单 (Owner decision list 2026-10-19 item 31): one compact line in the area's photo card — how many
// of the usual shots are answered — opening the list. Guidance only: nothing is blocked by an open shot.
export function PhotoGuideRow(props: { area: V2Area; target: EvidenceTarget; disabled?: boolean }) {
  const { t } = useT();
  const evidence = useStore(evidenceStore, (s) => s.evidence);
  const items = useStore(inspectionStore, (s) => s.items);
  const status = useMemo(() => photoGuideStatus(props.area, evidence, linkedTemplateIdsOf(items, props.area.id)), [props.area, evidence, items]);
  const [open, setOpen] = useState(false);
  if (status.total === 0) return null;
  return (
    <>
      <button type="button" className="photo-guide-row" onClick={() => setOpen(true)} disabled={props.disabled} data-testid="photo-guide-row" data-answered={status.answered} data-total={status.total}>
        <Icon name="format_list_bulleted" />
        <span className="photo-guide-row-text">{t('guide.row', { done: status.answered, total: status.total })}</span>
        <Icon name="chevron_right" />
      </button>
      {open ? <PhotoGuideSheet area={props.area} target={props.target} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function PhotoGuideSheet(props: { area: V2Area; target: EvidenceTarget; onClose: () => void }) {
  const { t, locale } = useT();
  const evidence = useStore(evidenceStore, (s) => s.evidence);
  const items = useStore(inspectionStore, (s) => s.items);
  const status = useMemo(() => photoGuideStatus(props.area, evidence, linkedTemplateIdsOf(items, props.area.id)), [props.area, evidence, items]);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function take(row: ShotStatus, from: 'camera' | 'library') {
    setBusy(row.shot.key);
    setMessage(null);
    try {
      const target = { ...props.target, shot: row.shot.key };
      const outcome = from === 'camera' ? await captureFromCameraAction(target) : await importFromLibraryAction(target);
      if (outcome.kind === 'error') setMessage(outcome.message);
    } finally {
      setBusy(null);
    }
  }

  async function skip(row: ShotStatus, skipped: boolean) {
    setBusy(row.shot.key);
    try {
      await setShotSkippedAction(props.area.id, row.shot.key, skipped);
    } finally {
      setBusy(null);
    }
  }

  function stateText(row: ShotStatus): string {
    if (row.state === 'taken') return t('guide.taken', { n: row.photoCount });
    if (row.state === 'skipped') return row.shot.component ? t('guide.absent') : t('guide.notNeeded');
    return t('guide.open');
  }

  return (
    <SheetShell title={t('guide.title')} subtitle={t('guide.subtitle', { done: status.answered, total: status.total })} onClose={props.onClose} layer="over" testId="photo-guide-sheet">
      <p className="card-note">{t('guide.intro')}</p>
      {message ? <InlineMessage kind="err" text={message} /> : null}
      <div className="photo-guide-list">
        {status.shots.map((row) => (
          <div className="photo-guide-shot" key={row.shot.key} data-testid="photo-guide-shot" data-shot={row.shot.key} data-state={row.state}>
            <span className={`photo-guide-mark photo-guide-mark-${row.state}`} aria-hidden="true">
              <Icon name={row.state === 'taken' ? 'check_circle' : row.state === 'skipped' ? 'do_not_disturb_on' : 'radio_button_unchecked'} />
            </span>
            <div className="photo-guide-main">
              <p className="photo-guide-title">{locale === 'zh-CN' ? row.shot.zh : row.shot.en}</p>
              <p className="photo-guide-state">{stateText(row)}</p>
            </div>
            <div className="photo-guide-actions">
              <IconButton icon="photo_camera" label={t('guide.camera')} onClick={() => void take(row, 'camera')} disabled={busy !== null} tone="soft" testId="photo-guide-camera" />
              <IconButton icon="photo_library" label={t('guide.library')} onClick={() => void take(row, 'library')} disabled={busy !== null} tone="soft" testId="photo-guide-library" />
              {row.state === 'skipped' ? (
                <button type="button" className="link-inline" onClick={() => void skip(row, false)} disabled={busy !== null} data-testid="photo-guide-unskip">
                  {t('guide.undo')}
                </button>
              ) : row.state === 'open' ? (
                <button type="button" className="link-inline" onClick={() => void skip(row, true)} disabled={busy !== null} data-testid="photo-guide-skip">
                  {row.shot.component ? t('guide.markAbsent') : t('guide.markNotNeeded')}
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </SheetShell>
  );
}
